import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Bell } from "lucide-react";
import { useWS } from "@/context/WSContext";
import { useAuth } from "@/context/AuthContext";

const AlertsCtx = createContext(null);

const MAX_RECENT = 20;

export function AlertsProvider({ children }) {
    const { user } = useAuth();
    const { subscribe } = useWS();
    const [recent, setRecent] = useState([]); // [{id, severity, message, created_at, job_id}]
    const [unread, setUnread] = useState(0);
    const [banner, setBanner] = useState(null); // most recent new alert for banner display
    const bannerTimer = useRef(null);

    const markAllRead = useCallback(() => setUnread(0), []);

    const dismissBanner = useCallback(() => setBanner(null), []);

    useEffect(() => {
        if (!user) return;
        const unsub = subscribe((m) => {
            if (m.event !== "alert.new" || !m.data) return;
            const a = m.data;
            setRecent((prev) => {
                if (prev.find((x) => x.id === a.id)) return prev;
                return [a, ...prev].slice(0, MAX_RECENT);
            });
            setUnread((u) => u + 1);
            setBanner(a);
            if (bannerTimer.current) clearTimeout(bannerTimer.current);
            bannerTimer.current = setTimeout(() => setBanner(null), 8000);
            const title =
                a.severity === "error" || a.severity === "critical"
                    ? "🚨 Critical anomaly"
                    : a.severity === "warning"
                      ? "Anomaly detected"
                      : "New event";
            toast(title, {
                description: a.message,
                duration: 6000,
                className:
                    a.severity === "warning"
                        ? "border-[hsl(var(--warning))]"
                        : a.severity === "error"
                          ? "border-[hsl(var(--destructive))]"
                          : "",
            });
        });
        return unsub;
    }, [subscribe, user]);

    return (
        <AlertsCtx.Provider value={{ recent, unread, markAllRead, banner, dismissBanner }}>
            {children}
        </AlertsCtx.Provider>
    );
}

export function useAlerts() {
    const ctx = useContext(AlertsCtx);
    if (!ctx) throw new Error("useAlerts must be used inside AlertsProvider");
    return ctx;
}

export function AlertBanner() {
    const { banner, dismissBanner } = useAlerts();
    if (!banner) return null;
    const color =
        banner.severity === "error" || banner.severity === "critical"
            ? "border-[hsl(var(--destructive))] text-[hsl(var(--destructive))] bg-[hsl(var(--destructive))]/5"
            : banner.severity === "warning"
              ? "border-[hsl(var(--warning))] text-[hsl(var(--warning))] bg-[hsl(var(--warning))]/5"
              : "border-[hsl(var(--accent))] text-[hsl(var(--accent))] bg-[hsl(var(--accent))]/5";
    return (
        <div
            className={`flex items-center gap-3 border-b px-6 py-2 text-sm animate-fade-in ${color}`}
            data-testid="alert-banner"
        >
            <AlertTriangle size={14} />
            <span className="font-semibold uppercase tracking-widest text-[10px]">
                {banner.severity}
            </span>
            <span className="flex-1 text-foreground">{banner.message}</span>
            <button
                onClick={dismissBanner}
                className="font-mono text-[10px] uppercase tracking-widest opacity-70 hover:opacity-100"
                data-testid="alert-banner-dismiss"
            >
                dismiss
            </button>
        </div>
    );
}

export function AlertBell() {
    const { unread, markAllRead, recent } = useAlerts();
    const [open, setOpen] = useState(false);
    return (
        <div className="relative">
            <button
                onClick={() => {
                    setOpen((o) => !o);
                    markAllRead();
                }}
                data-testid="alert-bell"
                className="relative rounded-sm border border-border p-1.5 transition-colors hover:border-foreground/40"
                aria-label="Alerts"
            >
                <Bell size={14} />
                {unread > 0 && (
                    <span
                        className="absolute -right-1 -top-1 min-w-[14px] rounded-full bg-[hsl(var(--destructive))] px-1 text-[9px] font-bold leading-[14px] text-white"
                        data-testid="alert-bell-count"
                    >
                        {unread > 9 ? "9+" : unread}
                    </span>
                )}
            </button>
            {open && (
                <div
                    className="absolute right-0 top-full z-40 mt-2 w-96 border border-border bg-surface shadow-xl"
                    data-testid="alert-bell-panel"
                >
                    <div className="flex items-center justify-between border-b border-border px-3 py-2">
                        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                            Recent alerts
                        </div>
                        <button
                            onClick={() => setOpen(false)}
                            className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground"
                        >
                            close
                        </button>
                    </div>
                    <div className="scroll-thin max-h-96 overflow-y-auto divide-y divide-border">
                        {recent.length === 0 ? (
                            <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                                No alerts yet.
                            </div>
                        ) : (
                            recent.map((a) => (
                                <div key={a.id} className="px-3 py-2">
                                    <div className="flex items-center justify-between">
                                        <span
                                            className={[
                                                "mb-pill",
                                                a.severity === "warning"
                                                    ? "border-[hsl(var(--warning))] text-[hsl(var(--warning))]"
                                                    : a.severity === "error" || a.severity === "critical"
                                                      ? "border-[hsl(var(--destructive))] text-[hsl(var(--destructive))]"
                                                      : "border-border text-muted-foreground",
                                            ].join(" ")}
                                        >
                                            {a.severity}
                                        </span>
                                        <span className="font-mono text-[10px] text-muted-foreground">
                                            {(a.created_at || "").slice(11, 19)}
                                        </span>
                                    </div>
                                    <div className="mt-1 text-xs">{a.message}</div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
